import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import { HlaWay } from "../../hla/models/HlaWay";
import { calculateGeographicHeading } from "../../map/utils/map-utils";
import { Coord } from "@turf/helpers";
import turf from "../utils/turf";
import {
  HlaLineProps,
  hlaWayLineLayerName,
} from "../../map/sources/HlaMapSource";
import { importHlaUtils } from "../../map/sources/import-hla/utils";
import { toast } from "../views/CustomToaster";
import { StreetViewMapSource } from "../../map/sources/StreetViewMapSource";
import { LineString } from "geojson";

interface DistanceToSelectedHla {
  currentPosition: GeoJSON.Position;
  currentPositionIsNear: boolean;
  nearestPointInLine: GeoJSON.Position;
  distanceToLine: number;
  distanceAlongLine: number;
  otherWayNearbyId: string | undefined;
}

export class StreetViewStore {
  rootStore;

  client?: google.maps.StreetViewPanorama;
  listeners: google.maps.MapsEventListener[] = [];

  // streetview
  distanceToSelectedHla?: DistanceToSelectedHla;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {});

    this.client = undefined;
    this.listeners.forEach((l) => l.remove());
  }

  init(container: HTMLElement, streetViewMapSource: StreetViewMapSource) {
    const { mapStore, hlaStore } = this.rootStore;
    const selectedHlaWay = hlaStore.getSelected();

    const initialPosition = selectedHlaWay
      ? this.extractHlaPosition(selectedHlaWay)
      : undefined;

    const client = new window.google.maps.StreetViewPanorama(container, {
      position: initialPosition?.position,
      pov: initialPosition?.pov,
      zoom: 1,
      addressControl: false,
      fullscreenControl: true,
      motionTracking: false,
      motionTrackingControl: false,
      showRoadLabels: true,
    });

    this.listeners.push(
      client.addListener("position_changed", () => {
        const p = client.getPosition();
        const position = p ? { lat: p.lat(), lng: p.lng() } : undefined;
        streetViewMapSource?.update({ position });
        this.calcDistanceToSelectedHla();

        if (p) {
          mapStore.map?.flyTo({
            center: [p.lng(), p.lat()],
            essential: true,
            zoom: 18,
          });
        }
      }),
    );

    this.listeners.push(
      client.addListener("pov_changed", () => {
        const currentPov = client.getPov?.();

        streetViewMapSource?.update({
          pov: {
            pitch: currentPov?.pitch ?? 0,
            heading: currentPov?.heading ?? 0,
          },
        });
      }),
    );

    runInAction(() => {
      this.client = client;
    });
  }

  goToHla(way: HlaWay) {
    if (this.client) {
      const geoData = this.extractHlaPosition(way);
      this.goTo(geoData.position, geoData.pov);
    }
  }

  goToLineString(lineString: LineString) {
    if (this.client) {
      const geoData = this.extractLineStringPosition(lineString);
      this.goTo(geoData.position, geoData.pov);
    }
  }

  goTo(position: google.maps.LatLngLiteral, pov: google.maps.StreetViewPov) {
    if (this.client) {
      this.client.setPosition(position);
      this.client.setPov(pov);
    }
  }

  extractHlaPosition(way: HlaWay) {
    const [lng, lat] = way.coordinates[0];
    const heading = calculateGeographicHeading(
      way.coordinates[0],
      way.coordinates[1],
    );
    return { position: { lat, lng }, pov: { heading, pitch: 0 } };
  }

  extractLineStringPosition(lineString: LineString) {
    const [lng, lat] = lineString.coordinates[0];
    const heading = calculateGeographicHeading(
      lineString.coordinates[0],
      lineString.coordinates[1],
    );
    return { position: { lat, lng }, pov: { heading, pitch: 0 } };
  }

  calcDistanceToSelectedHla() {
    const { hlaStore, mapStore } = this.rootStore;

    if (!this.client) return;

    const position = this.client.getPosition();

    if (!position) {
      return runInAction(() => {
        this.distanceToSelectedHla = undefined;
      });
    }

    const coord: Coord = [position.lng(), position.lat()];

    const selectedWay = hlaStore.getSelected();
    if (!selectedWay) return;

    const lineString = turf.lineString(selectedWay.coordinates);
    const nearestPointInLine = turf.nearestPointOnLine(lineString, coord, {
      units: "meters",
    });

    const nearbyRadius = 5; // meters
    const nearByWayIds = this.findWayIdsNearby(coord, nearbyRadius);

    runInAction(() => {
      this.distanceToSelectedHla = {
        currentPosition: coord,
        currentPositionIsNear:
          nearestPointInLine.properties.dist < nearbyRadius,
        nearestPointInLine: nearestPointInLine.geometry.coordinates,
        distanceAlongLine: nearestPointInLine.properties.lineDistance,
        distanceToLine: nearestPointInLine.properties.dist,
        otherWayNearbyId: nearByWayIds.find((id) => id !== selectedWay.id),
      };
    });

    mapStore.streetViewMapSource?.update({
      nearestPointInLine: nearestPointInLine.geometry.coordinates,
    });
  }

  private findWayIdsNearby(coord: Coord, radius: number): string[] {
    const { mapStore } = this.rootStore;

    if (!mapStore.map) return [];

    const point = turf.point(coord as number[]);
    const buffer = turf.buffer(point, radius, { units: "meters" })!;
    const bbox = importHlaUtils.polygonToBbox(buffer, mapStore.map!);

    const nearbyFeatures = mapStore.map.queryRenderedFeatures(bbox, {
      layers: [hlaWayLineLayerName],
    });

    return nearbyFeatures.map((feature) => {
      const props = feature.properties as HlaLineProps;
      return props.hlaWayId;
    });
  }

  async queryCurrentPosition() {
    if (!this.client) return;

    const { mapStore, hlaStore } = this.rootStore;

    const position = this.client.getPosition();
    const pov = this.client.getPov();

    if (!position || !pov) {
      toast("error", "Failed to retrive current position");
      return;
    }

    const importedWays =
      await mapStore.importHlaMapSource?.importFromStreetview(
        [position.lng(), position.lat()],
        pov.heading,
      );

    if (!importedWays) return;

    if (importedWays.length > 0) {
      hlaStore.select(importedWays[0].id);
    }
    this.calcDistanceToSelectedHla();
  }
}
